import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "@/lib/api";
import { ArrowDownRight, ArrowRight, ArrowUpRight, Minus } from "lucide-react";

const RANGES = [["day", "Day"], ["week", "Week"], ["month", "Month"], ["year", "Year"], ["all", "All time"]];

function Stat({ label, value, prev, testId }) {
  let delta = null;
  if (prev != null && typeof value === "number") {
    const diff = value - prev;
    const pct = prev === 0 ? (value > 0 ? 100 : 0) : Math.round((diff / prev) * 100);
    delta = { diff, pct };
  }
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testId}>
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="mt-2 flex items-end justify-between gap-2">
        <p className="font-serif text-4xl font-semibold text-foreground">{typeof value === "number" ? value.toLocaleString() : value}</p>
        {delta && (
          <span className={`mb-1 inline-flex items-center gap-0.5 font-mono text-[11px] ${delta.diff > 0 ? "text-emerald-600 dark:text-emerald-400" : delta.diff < 0 ? "text-destructive" : "text-muted-foreground"}`} title="vs previous period">
            {delta.diff > 0 ? <ArrowUpRight className="h-3 w-3" /> : delta.diff < 0 ? <ArrowDownRight className="h-3 w-3" /> : <Minus className="h-3 w-3" />}{Math.abs(delta.pct)}%
          </span>
        )}
      </div>
    </div>
  );
}

function fmtBucket(b, unit) {
  if (unit === "hour") return b.slice(11, 16);
  if (unit === "day") return new Date(`${b}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const [y, m] = b.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: "short", year: "2-digit" });
}

function QuestionList({ title, hint, rows, metric, testId }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-serif text-xl font-semibold text-foreground">{title}</h3>
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{hint}</span>
      </div>
      {rows.length === 0 ? <p className="mt-4 text-sm text-muted-foreground">Nothing in this period yet.</p> : (
        <ol className="mt-3 divide-y divide-border">
          {rows.map((r, i) => (
            <li key={r.question.id} className="flex items-center gap-3 py-2.5">
              <span className="w-5 shrink-0 font-mono text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
              <div className="min-w-0 flex-1">
                <Link to={`/question/${r.question.id}`} className="block truncate font-serif text-base text-foreground hover:text-primary" data-testid={`${testId}-q-${r.question.id}`}>“{r.question.question_kjv}”</Link>
                <p className="no-reftag font-mono text-[10px] text-muted-foreground">{r.question.reference}</p>
              </div>
              <span className="shrink-0 text-right font-mono text-xs text-foreground">{metric(r)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function AdminOverview({ onNavigate }) {
  const [range, setRange] = useState("week");
  const { data } = useQuery({ queryKey: ["admin-overview"], queryFn: async () => (await api.get("/admin/overview")).data });
  const { data: an } = useQuery({ queryKey: ["admin-analytics", range], queryFn: async () => (await api.get("/admin/analytics", { params: { range } })).data });
  if (!data) return <div className="h-40 animate-pulse rounded-2xl bg-muted" />;
  const q = data.today.question;
  const t = an?.totals;

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Questions" value={data.questions} testId="stat-questions" />
        <Stat label="Users" value={data.users} testId="stat-users" />
        <Stat label="Journal entries" value={data.journal_entries} testId="stat-entries" />
        <Stat label="Journaling users" value={data.journal_users} testId="stat-journal-users" />
      </div>

      <section className="space-y-4" data-testid="usage-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-serif text-2xl font-semibold text-foreground">Usage</h2>
            <p className="text-xs text-muted-foreground">Page views and visitors, with change versus the previous period.</p>
          </div>
          <div className="inline-flex overflow-hidden rounded-full border border-border bg-background font-mono text-[11px] uppercase tracking-wider" data-testid="range-switch">
            {RANGES.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setRange(k)} data-testid={`range-${k}`}
                className={`px-3 py-1.5 transition-colors ${range === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
        </div>
        {!an ? <div className="h-40 animate-pulse rounded-2xl bg-muted" /> : (
          <>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Stat label="Page views" value={t.views} prev={an.previous?.views} testId="stat-views" />
              <Stat label="Unique visitors" value={t.visitors} prev={an.previous?.visitors} testId="stat-visitors" />
              <Stat label="Signed-in active" value={t.signed_in} testId="stat-signed-in" />
              <Stat label="New sign-ups" value={t.new_users} testId="stat-new-users" />
              <Stat label="Entries written" value={t.entries} testId="stat-entries-written" />
              <Stat label="Questions viewed" value={t.questions_viewed} testId="stat-questions-viewed" />
              <Stat label="Logins" value={t.logins} testId="stat-logins" />
              <Stat label="Failed logins" value={t.failed_logins} testId="stat-failed-logins" />
            </div>
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="views-chart">
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Views over time · per {an.unit}</p>
              {an.series.length === 0 ? <p className="mt-6 text-sm text-muted-foreground">No visits recorded in this period yet.</p> : (
                <div className="mt-3 h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={an.series.map((s) => ({ ...s, label: fmtBucket(s.bucket, an.unit) }))} margin={{ left: -20, right: 8, top: 8 }}>
                      <defs>
                        <linearGradient id="views-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--gold))" stopOpacity={0.5} /><stop offset="100%" stopColor="hsl(var(--gold))" stopOpacity={0} /></linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
                      <Area type="monotone" dataKey="views" name="Views" stroke="hsl(var(--gold))" fill="url(#views-fill)" strokeWidth={2} />
                      <Area type="monotone" dataKey="visitors" name="Visitors" stroke="hsl(var(--primary))" fill="transparent" strokeWidth={1.5} strokeDasharray="4 3" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div className="grid gap-4 lg:grid-cols-3">
              <QuestionList title="Most viewed" hint="views" rows={an.top_viewed} metric={(r) => `${r.views} · ${r.visitors} people`} testId="top-viewed" />
              <QuestionList title="Most journaled" hint="entries · mentions" rows={an.top_journaled} metric={(r) => `${r.entries}${r.mentions ? ` · @${r.mentions}` : ""}`} testId="top-journaled" />
              <QuestionList title="Most popular" hint="views + saves + entries" rows={an.most_popular} metric={(r) => `${r.score}`} testId="most-popular" />
            </div>
          </>
        )}
      </section>

      <div className="grain relative overflow-hidden rounded-2xl bg-secondary p-6 text-secondary-foreground sm:p-8" data-testid="overview-today">
        <div className="relative z-10">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-gold">
            Today&apos;s Daily Spark · {data.today.date} · {data.today.source === "scheduled" ? "hand-picked" : "auto (random for the day)"}
          </p>
          <p className="mt-3 font-serif text-2xl italic text-white sm:text-3xl">&ldquo;{q.question_kjv}&rdquo;</p>
          <p className="mt-2 text-sm text-white/70">{q.reference} · {q.who_asked.join(", ")}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button type="button" onClick={() => onNavigate("daily")} data-testid="overview-manage-daily" className="inline-flex items-center gap-1 rounded-full bg-white px-4 py-2 text-sm font-semibold text-secondary">
              Manage the calendar <ArrowRight className="h-4 w-4" />
            </button>
            <Link to={`/question/${q.id}`} className="inline-flex items-center rounded-full border border-white/30 px-4 py-2 text-sm text-white hover:bg-white/10">View question</Link>
          </div>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        {data.scheduled_days} upcoming day{data.scheduled_days === 1 ? "" : "s"} hand-picked. Days without a pick use a stable random question.
      </p>
    </div>
  );
}
